import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiStar, FiShoppingCart, FiHeart, FiArrowLeft, FiCheck, FiTruck, FiShield } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { products } from '../data/products';
import ProductCard from '../components/ProductCard';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useCart();
  const { addToast } = useToast();

  const product = products.find(p => p.id === parseInt(id));
  const [selectedColor, setSelectedColor] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [wishlist, setWishlist] = useState(false);

  if (!product) {
    return (
      <div className="not-found-page container">
        <h1>Product not found</h1>
        <Link to="/shop" className="btn btn-primary">Back to Shop</Link>
      </div>
    );
  }

  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 3);
  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      dispatch({ type: 'ADD_TO_CART', payload: product });
    }
    dispatch({ type: 'OPEN_DRAWER' });
    addToast(`${product.name} added to cart! 🛍️`);
  };

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button onClick={() => navigate(-1)} className="breadcrumb-back">
            <FiArrowLeft /> Back
          </button>
          <span className="breadcrumb-sep">/</span>
          <Link to="/shop">Shop</Link>
          <span className="breadcrumb-sep">/</span>
          <span>{product.category}</span>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{product.name}</span>
        </div>

        {/* Product Grid */}
        <div className="product-detail-grid">
          {/* Image */}
          <motion.div
            className="product-detail-img-wrapper"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={product.image} alt={product.name} className="product-detail-img" />
            {product.badge && (
              <span className={`product-badge badge-${product.badge.toLowerCase().replace(' ', '-')}`}>
                {product.badge}
              </span>
            )}
            {discount > 0 && (
              <span className="product-discount">-{discount}%</span>
            )}
          </motion.div>

          {/* Info */}
          <motion.div
            className="product-detail-info"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="product-category">{product.category}</span>
            <h1 className="product-detail-name">{product.name}</h1>

            {/* Rating */}
            <div className="product-rating">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <FiStar
                    key={i}
                    size={16}
                    fill={i < Math.floor(product.rating) ? '#F59E0B' : 'none'}
                    stroke={i < Math.floor(product.rating) ? '#F59E0B' : '#666'}
                  />
                ))}
              </div>
              <span className="rating-text">{product.rating} · {product.reviews.toLocaleString()} reviews</span>
            </div>

            {/* Price */}
            <div className="detail-price-row">
              <span className="detail-price">¥{product.price}</span>
              {product.originalPrice > product.price && (
                <>
                  <span className="detail-original-price">¥{product.originalPrice}</span>
                  <span className="detail-savings">You save ¥{(product.originalPrice - product.price).toFixed(2)}</span>
                </>
              )}
            </div>

            {/* Description */}
            <p className="detail-description">{product.description}</p>

            {/* Colors */}
            <div className="detail-option">
              <label>Color</label>
              <div className="color-options">
                {product.colors.map((color, i) => (
                  <button
                    key={i}
                    className={`color-swatch ${selectedColor === i ? 'selected' : ''}`}
                    style={{ background: color }}
                    onClick={() => setSelectedColor(i)}
                    aria-label={`Color ${i + 1}`}
                  >
                    {selectedColor === i && <FiCheck size={12} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="detail-option">
              <label>Quantity</label>
              <div className="qty-selector">
                <button className="qty-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                <span className="qty-value">{quantity}</span>
                <button className="qty-btn" onClick={() => setQuantity(quantity + 1)}>+</button>
              </div>
            </div>

            {/* Stock */}
            <div className="stock-indicator">
              <div className={`stock-dot ${product.stock < 15 ? 'low-stock' : 'in-stock'}`} />
              <span>{product.stock < 15 ? `Only ${product.stock} left!` : 'In Stock'}</span>
            </div>

            {/* Actions */}
            <div className="detail-actions">
              <motion.button
                className="btn btn-primary btn-lg flex-1"
                onClick={handleAddToCart}
                whileTap={{ scale: 0.97 }}
              >
                <FiShoppingCart /> Add to Cart
              </motion.button>
              <motion.button
                className={`btn btn-outline wishlist-btn ${wishlist ? 'wishlisted' : ''}`}
                onClick={() => {
                  setWishlist(!wishlist);
                  addToast(wishlist ? 'Removed from wishlist' : '❤️ Added to wishlist!');
                }}
                whileTap={{ scale: 0.97 }}
              >
                <FiHeart fill={wishlist ? 'currentColor' : 'none'} />
              </motion.button>
            </div>

            {/* Trust Badges */}
            <div className="trust-badges">
              <div className="trust-badge">
                <FiTruck /> Free Shipping
              </div>
              <div className="trust-badge">
                <FiShield /> 2 Year Warranty
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="product-tabs">
          <div className="tabs-header">
            {['description', 'features', 'reviews'].map(tab => (
              <button
                key={tab}
                className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <div className="tab-content">
            {activeTab === 'description' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p>{product.description}</p>
                <p>Crafted with precision and care, this {product.name.toLowerCase()} represents the pinnacle of modern design and functionality. Every detail has been considered to ensure the highest quality experience for our customers.</p>
              </motion.div>
            )}
            {activeTab === 'features' && (
              <motion.ul className="features-list" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {product.features.map((f, i) => (
                  <li key={i}><FiCheck className="feature-check" /> {f}</li>
                ))}
              </motion.ul>
            )}
            {activeTab === 'reviews' && (
              <motion.div className="reviews-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="reviews-summary">
                  <div className="rating-big">{product.rating}</div>
                  <div>
                    <div className="stars-big">
                      {[...Array(5)].map((_, i) => (
                        <FiStar key={i} size={20} fill={i < Math.floor(product.rating) ? '#F59E0B' : 'none'} stroke="#F59E0B" />
                      ))}
                    </div>
                    <span>Based on {product.reviews.toLocaleString()} reviews</span>
                  </div>
                </div>
                <div className="review-cards">
                  {[
                    { name: 'Alex M.', rating: 5, text: 'Absolutely love this product! The quality is outstanding and it arrived quickly.' },
                    { name: 'Sarah K.', rating: 5, text: 'Best purchase I\'ve made this year. Worth every penny!' },
                    { name: 'James L.', rating: 4, text: 'Great product, very happy with the quality and the fast shipping.' },
                  ].map((review, i) => (
                    <div key={i} className="review-card">
                      <div className="review-header">
                        <span className="reviewer-name">{review.name}</span>
                        <div className="stars">
                          {[...Array(5)].map((_, j) => (
                            <FiStar key={j} size={12} fill={j < review.rating ? '#F59E0B' : 'none'} stroke="#F59E0B" />
                          ))}
                        </div>
                      </div>
                      <p className="review-text">{review.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="related-section section">
            <h2 className="section-title">You May Also Like</h2>
            <div className="products-grid">
              {related.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
